import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import { useShiftAddEditStore } from '@libs/store/shifts';
import { Qualification } from '@libs/models/talents';
import { AvatarList, DataGrid, GridColDef } from '@molecules/dataDisplay';
import { ShiftStatusChip } from '@atoms/dataDisplay';
import { CloseIcon } from '@atoms/icons';
import { Button } from '@atoms/buttons';
import { Flex } from '@atoms/layout';
import { AssignToMultipleShiftsDialog, UnassignDialog } from '@pages/schedule/dialogs';
import { ShiftAssigneeButton, ShiftType } from '@pages/schedule/components';
import { ShiftRecurrent } from '@libs/models/shifts/series';
import { ShiftOld } from '@libs/models/shifts/shift';
import { getUniqueObjects } from '@libs/utils';
import { Box } from '@mui/material';

type ShiftListProps = {
  list?: ShiftListItem[];
  isMultiAssign?: boolean;
  loading: boolean;
  locationId?: string;
  qualifications?: string[];
  canUnassign?: boolean;
  parentShift: ShiftRecurrent | null;
  onAssignClick?: (selectedShifts: string[], assignee: string) => Promise<void>;
  onUnassignClick: (shiftid: string, reason: string, comment: string) => Promise<void>;
  onShiftChanged?: () => void;
};
export function ShiftsList({
  list = [],
  loading,
  onAssignClick,
  onShiftChanged,
  locationId,
  isMultiAssign,
  qualifications,
  canUnassign,
  parentShift,
  onUnassignClick,
}: ShiftListProps) {
  const assignee = list.map((item) => item.assignee).filter((item) => item !== null);
  const uniqueAssignee = Array.from(new Set(assignee.map((item) => item?.id)))
    .map((id) => assignee.find((item) => item?.id === id))
    .filter((item) => item !== undefined);

  const isAssigned = uniqueAssignee.length > 0;

  const [selected, setSelected] = useState<string[]>([]);
  const [opedAssignDialog, setOpedAssignDialog] = useState(false);
  const [openUnassignDialog, setOpenUnassignDialog] = useState(false);
  const openShiftDrawer = useShiftAddEditStore((s) => s.openShiftDrawer);
  const onShiftChangedInternal = () => {
    onShiftChanged?.();
    setSelected([]);
  };

  const shouldShowExternalAssignButton = parentShift?.assignRequest?.status === 'Pending';
  const shouldShowUnassignButton = isAssigned && !isMultiAssign && !shouldShowExternalAssignButton;
  const shouldShowAssignButton = !isAssigned && !shouldShowExternalAssignButton;

  return (
    <>
      <Flex
        column
        sx={{ height: '100%', width: '100%' }}
        px={3}
        pb={3}
        pt={2}
        bgcolor={(theme) => `color-mix(in srgb, #fff 100%, ${theme.palette.primary.main} 8%)`}
      >
        <Stack direction="row" alignItems={'center'} mb={2} gap={2}>
          {!shouldShowExternalAssignButton && (
            <>
              <Collapse in={!loading}>
                <Flex gap={1} alignItems="center">
                  {uniqueAssignee.length > 0 ? (
                    <AvatarList list={uniqueAssignee} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      <FormattedMessage id="shifts.noAssignees" />
                    </Typography>
                  )}
                </Flex>
              </Collapse>
              <Flex flex={1} />
              <Collapse in={!!selected.length}>
                <Flex alignCenter gap={1}>
                  <IconButton sx={{ flexShrink: 0 }} onClick={() => setSelected([])}>
                    <CloseIcon fontSize={14} width={14} />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="shifts.editSeriesDrawer.selectedShifts"
                      values={{ number: selected.length }}
                    />
                  </Typography>
                </Flex>
              </Collapse>
              <Box flex={1} />
            </>
          )}

          {parentShift && shouldShowExternalAssignButton && (
            <ShiftAssigneeButton
              shift={{
                ...parentShift,
                id: parentShift.id,
                qualifications: parentShift.qualifications.map((q) => ({
                  ...q,
                  name: (q as any).name || q.abbreviation,
                  category: (q as any).category || 'General',
                })),
                departmentNode: {
                  id: '',
                  name: parentShift.department,
                },
                subDepartmentNode: {
                  id: '',
                  name: parentShift.subDepartment || '',
                },
                canHasMultipleAssignee: false,
                isArchive: parentShift.isArchived,
                timeZone: 'UTC',
                status: 'na',
                specialty: {
                  id: '',
                  name: parentShift.speciality || '',
                  category: 'General',
                },
                notes: parentShift.notes.map((note) => note.value).join('\n'),
                assignee:
                  parentShift.assignees && parentShift.assignees.length
                    ? {
                        ...parentShift.assignees[0],
                        agencyId: (parentShift.assignees[0] as any).agencyId,
                        agencyName: (parentShift.assignees[0] as any).agencyName,
                        firstName: (parentShift.assignees[0] as any).firstName,
                        lastName: (parentShift.assignees[0] as any).lastName,
                        id: (parentShift.assignees[0] as any).id,
                        imageUrl: (parentShift.assignees[0] as any).avatar,
                        isInternal: (parentShift.assignees[0] as any).isInternal,
                        orientedList: (parentShift.assignees[0] as any).orientedList,
                        avatar: (parentShift.assignees[0] as any).imageUrl,
                        name: (parentShift.assignees[0] as any).fullName,
                      }
                    : null,
                assignees: parentShift.assignees.length
                  ? getUniqueObjects(parentShift.assignees).map((assignee) => ({
                      ...assignee,
                      agencyId: (assignee as any).agencyId,
                      agencyName: (assignee as any).agencyName,
                      firstName: (assignee as any).firstName,
                      lastName: (assignee as any).lastName,
                      id: (assignee as any).id,
                      imageUrl: (assignee as any).avatar,
                      isInternal: (assignee as any).isInternal,
                      orientedList: (assignee as any).orientedList,
                      avatar: (assignee as any).imageUrl,
                      name: (assignee as any).fullName,
                    }))
                  : parentShift.assignRequest
                    ? [
                        {
                          ...parentShift.assignRequest.staff,
                          imageUrl: parentShift.assignRequest.staff.avatar,
                          name: [parentShift.assignRequest.staff.firstName, parentShift.assignRequest.staff.lastName]
                            .filter(Boolean)
                            .join(' '),
                        },
                      ]
                    : [],
                shiftLength: 0,
                shiftStart: new Date(),
                shiftEnd: new Date(),
                recurrenceId: parentShift.id,
                recurrence: parentShift.id.includes('series_') ? 'series' : 'rotation',
              }}
            />
          )}
          {shouldShowUnassignButton && (
            <Button
              variant="contained"
              onClick={() => setOpenUnassignDialog(true)}
              sx={{ px: 6, flexShrink: 0 }}
              startIcon={<CloseIcon fontSize={14} width={14} color="text.disabled" />}
              disabled={!canUnassign}
            >
              <FormattedMessage id="shifts.unassignBtnLabel" />
            </Button>
          )}
          {shouldShowAssignButton && (
            <Button
              variant="contained"
              onClick={() => setOpedAssignDialog(true)}
              sx={{ px: 6, flexShrink: 0 }}
              disabled={isMultiAssign ? !selected.length : isAssigned || false}
            >
              <FormattedMessage id="shifts.assignBtnLabel" />
            </Button>
          )}
        </Stack>
        <Flex height={'100%'} minHeight={0}>
          <DataGrid
            rows={list ?? []}
            paginationMode="client"
            sortingMode="client"
            pageSizeOptions={[10, 20]}
            loading={loading}
            rowHeight={64}
            columns={useShiftsListColumns({
              onAssigned: onShiftChangedInternal,
              onUnassigned: onShiftChangedInternal,
              isAssignable: isMultiAssign,
              parentShift,
            })}
            checkboxSelection={isMultiAssign}
            checkboxSelectionVisibleOnly
            onRowSelectionModelChange={(params) => {
              setSelected(params.map((p) => p.toString()));
            }}
            isRowSelectable={({ row }) => !row.assignee && !!parentShift?.canAssign}
            onRowClick={(params) => {
              openShiftDrawer(`${params.id}`);
            }}
            rowSelectionModel={selected}
            disableRowSelectionOnClick
          />
        </Flex>
      </Flex>

      <AssignToMultipleShiftsDialog
        open={opedAssignDialog}
        shiftsNumber={isMultiAssign ? selected.length : list.length}
        onClose={() => setOpedAssignDialog(false)}
        allowOnlyInternals={true} //!!isMultiAssign}
        onSubmit={async (v) => {
          await onAssignClick?.(selected, v);
          setSelected([]);
          setOpedAssignDialog(false);
        }}
        loading={loading}
        locationId={locationId}
        qualifications={qualifications}
      />
      {parentShift && (
        <UnassignDialog
          open={openUnassignDialog}
          shift={parentShift}
          onSubmit={async (reason, reasonComment) => {
            await onUnassignClick?.(parentShift.id, reason, reasonComment);
            setOpenUnassignDialog(false);
          }}
          loading={loading}
          onClose={() => setOpenUnassignDialog(false)}
        />
      )}
    </>
  );
}

function useShiftsListColumns({
  onAssigned,
  onUnassigned,
  isAssignable,
  parentShift,
}: {
  onAssigned?: () => void;
  onUnassigned?: () => void;
  isAssignable?: boolean;
  parentShift: ShiftRecurrent | null;
}) {
  const intl = useIntl();

  // Columns should be memoized to prevent table flickering and state loss
  return useMemo(() => {
    const columns: GridColDef<ShiftListItem>[] = [
      getDateColumn(intl),
      getShiftTypeColumn(intl),
      getStatusColumn(intl),
      ...(isAssignable ? [getAssigneeColumn(intl, parentShift, { onAssigned, onUnassigned })] : []),
    ];
    return columns;
  }, [intl, onAssigned, onUnassigned]);
}

function getDateColumn(intl: IntlShape): GridColDef<ShiftListItem> {
  return {
    field: 'startDate',
    headerName: intl.formatMessage({
      id: 'shifts.fields.date',
    }),
    flex: 1,
    minWidth: 80,
    renderCell: (params) => {
      if (!params.row) {
        return null;
      }
      const row = params.row;
      const formattedStart = dayjs(row.startDate).format('MM.DD.YYYY');
      const weekday = dayjs(row.startDate).format('dddd');
      return (
        <Stack direction={'column'} justifyContent={'center'} height={'100%'}>
          <Typography variant="body2">{formattedStart}</Typography>
          <Typography variant="caption">{weekday}</Typography>
        </Stack>
      );
    },
  };
}

function getShiftTypeColumn(intl: IntlShape): GridColDef<ShiftListItem> {
  return {
    field: 'shiftStart',
    headerName: intl.formatMessage({
      id: 'shifts.fields.shiftType',
    }),
    flex: 1,
    minWidth: 160,
    renderCell: (params) => {
      return (
        <ShiftType
          startTime={dayjs(params.row.startDate)}
          endTime={dayjs(params.row.endDate)}
          boxProps={{ height: '100%' }}
        />
      );
    },
  };
}

function getStatusColumn(intl: IntlShape): GridColDef<ShiftListItem> {
  return {
    field: 'status',
    headerName: intl.formatMessage({ id: 'shifts.fields.status' }),
    flex: 1,
    minWidth: 80,
    renderCell: (params) => {
      if (!params.value) {
        return null;
      }

      return <ShiftStatusChip value={params.value} />;
    },
  };
}

function getAssigneeColumn(
  intl: IntlShape,
  parentShift: ShiftRecurrent | null,
  {
    onAssigned,
    onUnassigned,
  }: {
    onAssigned?: () => void;
    onUnassigned?: () => void;
  },
): GridColDef<ShiftListItem, ShiftListItem['assignee']> {
  return {
    field: 'assignee',
    headerName: intl.formatMessage({ id: 'shifts.fields.assignee' }),
    flex: 1,
    // To prevent assignee column component to collapse
    minWidth: 165,
    sortComparator: (v1, v2) => {
      if (!v1) {
        return 0;
      }
      if (!v2) {
        return 1;
      }
      return `${v1.name}`.localeCompare(`${v1.name}`);
    },
    renderCell: ({ row }) => {
      const disableUnassign = parentShift?.canUnassign;
      const allowAssign = parentShift?.canAssign;
      const shift = row as unknown as ShiftOld;
      return (
        <ShiftAssigneeButton
          shift={{
            ...shift,
            id: shift.id,
            assignees: shift.assignee ? [shift.assignee] : [],
            qualifications: shift.qualifications,
            shiftLength: dayjs(row.endDate).diff(dayjs(row.startDate), 'millisecond'),
            shiftStart: dayjs(row.startDate).tz(row.timeZone).toDate(),
          }}
          allowOnlyInternals={true}
          disabled={row.assignee ? disableUnassign : !allowAssign}
          onAssigned={onAssigned}
          onUnassigned={onUnassigned}
        />
      );
    },
  };
}

type ShiftListItem = {
  id: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  status: string;
  assignee: {
    id: string;
    name: string;
    avatar: string;
  } | null;
  qualifications: Qualification[];
  isParentArchived: boolean;
};

function isShiftAllowedForAssignment(d: { status: string; isParentArchived: boolean }): boolean {
  return ['Unassigned', 'Pending', 'Opened'].includes(d.status) || (d.isParentArchived && d.status === 'Unfilled');
}
